/* eslint-disable react/prop-types -- props validated via TypeScript StepProps */
import { useMemo } from 'react';
import { Control, Controller, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import Select from 'components/Common/Select';
import Input from 'components/Common/Input';
import { Box, Typography, Button, Paper, CircularProgress, useTheme } from '@mui/material';
import { FaChevronRight } from 'react-icons/fa';
import IndustryIcon from '@/assets/icons/IndustryIcon';
import SpecialityIcon from '@/assets/icons/SpecialityIcon';
import PracticeManagementIcon from '@/assets/icons/PracticeManagementIcon';
import { useGetPublicAvailableIntegrations } from 'apis/agency/businessOnboarding/hooks';
import { IoIosWarning } from 'react-icons/io';

const INDUSTRIES = [
  { label: 'Healthcare', value: 'healthcare' },
  { label: 'Beauty & Wellness', value: 'beauty_and_wellness' },
  { label: 'Field Services', value: 'field_service' },
  { label: 'Restaurant', value: 'restaurant' },
  { label: 'Others', value: 'others' }
] as const;

const SPECIALTIES_BY_INDUSTRY: Record<string, { label: string; value: string }[]> = {
  healthcare: [
    { label: 'Dental', value: 'healthcare_dental_care' },
    { label: 'Primary', value: 'healthcare_primary_care' },
    { label: 'Mental', value: 'mental_healthcare' },
    { label: 'Other', value: 'healthcare_default' }
  ],
  restaurant: [
    { label: 'Fast-Food', value: 'restaurant_fast_food' },
    { label: 'Fine-Dining', value: 'restaurant_fine_dining' },
    { label: 'Other', value: 'restaurant_default' }
  ],
  field_service: [
    { label: 'Plumbing', value: 'field_service_plumbing' },
    { label: 'HVAC', value: 'field_service_hvac' },
    { label: 'Other', value: 'field_service_default' }
  ],
  beauty_and_wellness: [
    { label: 'Salon', value: 'salon_or_barber_shop' },
    { label: 'Other', value: 'general_customer_support' }
  ],
  others: [{ label: 'General', value: 'general_customer_support' }]
};

const SOFTWARE_LABELS: Record<string, string> = {
  healthcare: 'Practice Management Software',
  restaurant: 'POS / Restaurant Software',
  field_service: 'Field Service Software',
  beauty_and_wellness: 'Booking Software',
  others: 'Business Software'
};

interface StepProps {
  onNext: () => void | Promise<void>;
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  isSubmitting?: boolean;
}

const BusinessDetailsStep: React.FC<StepProps> = ({
  onNext,
  control,
  setValue,
  watch,
  isSubmitting = false
}: StepProps) => {
  const theme = useTheme();

  const chipSx = {
    px: 3,
    py: 1.2,
    borderRadius: '30px',
    border: '1px solid #e5e7eb',
    cursor: 'pointer',
    background: '#fff',
    fontSize: '14px',
    fontWeight: 500,
    transition: 'background 0.15s ease, color 0.15s ease, border-color 0.15s ease'
  } as const;

  const chipSelectedSx = {
    background: theme.palette.primary.main,
    color: '#fff',
    borderColor: theme.palette.primary.main
  } as const;

  const industry = watch('industry') as (typeof INDUSTRIES)[number]['value'] | '' | undefined;
  const specialityOptions = industry && SPECIALTIES_BY_INDUSTRY[industry] ? SPECIALTIES_BY_INDUSTRY[industry] : [];

  const { data: allIntegrationsData, isLoading: loadingIntegrations } = useGetPublicAvailableIntegrations({
    per_page: 999,
    'filter[industries_contain]': industry || 'healthcare'
  });

  const softwareOptions = useMemo(() => {
    const allOptions = (allIntegrationsData?.data || []).map((item) => ({
      label: item.name,
      value: item.id.toString()
    }));

    return [{ label: 'No Integration', value: ' ' }, ...allOptions, { label: 'Other', value: 'other' }];
  }, [allIntegrationsData]);

  const currentSoftwareLabel = industry ? SOFTWARE_LABELS[industry] : 'Practice Management Software';
  const selectedIntegrationId = watch('available_integration_ids[0]');

  return (
    <Paper sx={{ p: 3, borderRadius: 3, width: '100%' }}>
      <Typography fontWeight={600} sx={{ fontSize: { xs: '16px', md: '24px' } }}>
        Tell us about your business
      </Typography>

      <Typography lineHeight='normal' fontSize='14px' color='text.secondary' mt={1}>
        This helps us pre-train your AI agent with industry knowledge so it sounds natural from the start.
      </Typography>

      {/* INDUSTRY */}
      <Typography display='flex' alignItems='center' gap={0.5} fontSize='14px' mt={3} fontWeight={400}>
        <IndustryIcon /> Industry{' '}
        <Box component='span' sx={{ color: '#DA0808' }}>
          *
        </Box>
      </Typography>

      <Controller
        name='industry'
        control={control}
        rules={{ required: 'Industry is required' }}
        render={({ field, fieldState }) => (
          <>
            <Box component='div' role='group' aria-label='Industry' display='flex' gap={2} flexWrap='wrap' mt={1}>
              {INDUSTRIES.map((item) => {
                const selected = field.value === item.value;
                return (
                  <Box
                    key={item.value}
                    component='button'
                    type='button'
                    onClick={() => {
                      if (field.value !== item.value) {
                        field.onChange(item.value);
                        setValue('faq_question_set', '', { shouldValidate: false, shouldDirty: true });
                        setValue('available_integration_ids[0]', '', { shouldValidate: false, shouldDirty: true });
                      }
                    }}
                    sx={{
                      ...chipSx,
                      ...(selected ? chipSelectedSx : {})
                    }}
                  >
                    {item.label}
                  </Box>
                );
              })}
            </Box>
            {fieldState.error && (
              <Typography color='error' fontSize='12px' mt={0.5}>
                {fieldState.error.message}
              </Typography>
            )}
          </>
        )}
      />

      {/* SPECIALITY */}
      <Typography display='flex' alignItems='center' gap={0.5} fontSize='14px' mt={3} fontWeight={400}>
        <SpecialityIcon /> Speciality{' '}
        <Box component='span' sx={{ color: '#DA0808' }}>
          *
        </Box>
      </Typography>

      <Controller
        name='faq_question_set'
        control={control}
        rules={{ required: 'Speciality is required' }}
        render={({ field, fieldState }) => (
          <>
            <Box component='div' role='group' aria-label='Speciality' display='flex' gap={2} flexWrap='wrap' mt={1}>
              {specialityOptions.map((item) => {
                const selected = field.value === item.value;
                return (
                  <Box
                    key={item.value}
                    component='button'
                    type='button'
                    disabled={!industry}
                    onClick={() => field.onChange(item.value)}
                    sx={{
                      ...chipSx,
                      ...(selected ? chipSelectedSx : {}),

                      opacity: industry ? 1 : 0.5,
                      cursor: industry ? 'pointer' : 'not-allowed'
                    }}
                  >
                    {item.label}
                  </Box>
                );
              })}
            </Box>
            {!industry && (
              <Typography color='text.secondary' fontSize='12px' mt={0.5}>
                Select an industry first
              </Typography>
            )}
            {fieldState.error && (
              <Typography color='error' fontSize='12px' mt={0.5}>
                {fieldState.error.message}
              </Typography>
            )}
          </>
        )}
      />

      {/* SOFTWARE */}
      <Typography mb={2} display='flex' alignItems='center' gap={0.5} fontSize='14px' mt={3} fontWeight={400}>
        <PracticeManagementIcon /> {currentSoftwareLabel}
      </Typography>

      <Select
        name='available_integration_ids[0]'
        control={control}
        label={currentSoftwareLabel}
        required
        sortOrder='none'
        disabled={loadingIntegrations}
        rules={{ required: `${currentSoftwareLabel} is required` }}
        options={softwareOptions}
      />

      {selectedIntegrationId === 'other' && (
        <Box mt={2}>
          <Input
            name='other_integration_name'
            control={control}
            label='Software Name'
            placeholder='Software Name'
            fullWidth
            required
            rules={{ required: 'Software name is required' }}
          />
          <Box
            mt={1.5}
            p={2}
            sx={{
              bgcolor: '#fff9e6',
              border: '1px solid #ffe58f',
              borderRadius: 2,
              display: 'flex',
              gap: 1.5,
              alignItems: 'flex-start'
            }}
          >
            <Box sx={{ color: '#faad14', mt: 0.2 }}>
              <IoIosWarning />
            </Box>
            <Typography fontSize='14px' color='#faad14' lineHeight='1.6'>
              We don't guarantee integration with unlisted software. If we find integration is not possible and we're
              unable to serve you, we will refund your onboarding fee.
            </Typography>
          </Box>
        </Box>
      )}

      {/* CTA */}
      <Button
        variant='contained'
        fullWidth
        disabled={isSubmitting}
        sx={{
          mt: 3,
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          gap: 1,
          background: theme.palette.primary.main
        }}
        onClick={onNext}
      >
        {isSubmitting ? (
          <CircularProgress size={20} color='inherit' />
        ) : (
          <>
            Continue <FaChevronRight />
          </>
        )}
      </Button>
    </Paper>
  );
};

export default BusinessDetailsStep;
